import { Flex, Spacer } from '@chakra-ui/react';
import React, { useLayoutEffect, useRef } from 'react';

import {
  MenuManagementsAccordionItem,
  MenuManagementSingleSelectInput,
} from '@/components/templates/PageLayout/Sidebar/MenuManagementsAccordionItem';
import {
  ChartIcon,
  CmAccordion,
  CmBox,
  CmFlex,
  CmLink,
  CmScrollBox,
  DeliveryIcon,
  DishIcon,
  ExternalLinkIcon,
  SettingsIcon,
  StoreIcon,
} from '@/components/uiParts';
import { LayerGroupIcon } from '@/components/uiParts';
import { ShippingBagIcon } from '@/components/uiParts/Icon/ShippingBag';
import { BULK_SOLDOUT_V2_ENTERPRISE_IDS, isProd } from '@/global/env';
import { routeParrentPath, routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useEnterprise } from '@/services/Enterprise/hooks';
import { useSidebarState } from '@/store/sidebar';

import { AccordionLink } from './AccordionLinkButton';
import { AccordionPanel } from './AccordionPanel';
import { CustomAccordionItem } from './CustomAccordionItem';
import { Logo } from './Logo';
import { sidebarList } from './sidebarList';
import { TextIcon } from './TextIcon';

type Props = {
  associateBrandSelectInput?: MenuManagementSingleSelectInput;
};

export const Sidebar: React.FC<Props> = ({ associateBrandSelectInput }) => {
  const { managerAccountMe } = useManagerAccountMe();

  const canUseLinkItems = managerAccountMe
    ? managerAccountMe.available_features.some(
        (f) => f === 'setting_sales_state_group'
      )
    : false;

  const { enterprise } = useEnterprise();

  const logoWrapperRef = useRef<HTMLDivElement>(null);
  const logoWrapperHeight = logoWrapperRef.current?.offsetHeight ?? 0;

  const {
    ordersData,
    menuManagement,
    store,
    external,
    setting,
    camelOrder,
    camelKiosk,
    delivery,
    salesReport,
  } = sidebarList(enterprise, managerAccountMe);

  const { LinkItems, LinkItemsV2, Home } = routes;

  // 注文データが有効かどうか
  const isOrdersDataPageEnabled = ordersData.childPageList.some(
    (page) => !page.isDisabled
  );
  // 売上レポートページが有効かどうか
  const isSalesReportPageEnabled = !salesReport.childPageList.some(
    (page) => page.isDisabled
  );
  // Camel Kioskページが有効かどうか
  const isCamelKioskPageEnabled = camelKiosk.childPageList.some(
    (page) => !page.isDisabled
  );

  const canUseLinkItemsV2 =
    !isProd ||
    (managerAccountMe !== undefined &&
      BULK_SOLDOUT_V2_ENTERPRISE_IDS.includes(managerAccountMe.enterprise_id));

  const { expandedIndices, setExpandedIndices, initialize } = useSidebarState();

  // 初回マウント時のみURLベースの初期値でstoreを初期化する
  // 描画前に実行し、全閉じ→展開のちらつきを防止する
  useLayoutEffect(() => {
    if (managerAccountMe === undefined) return;

    // サイドバーの各アコーディオンセクションを定義。
    // enabledフラグで条件付きセクション（売上レポート、注文データ、Kiosk）の表示/非表示を管理。
    // ※ この配列の順序はJSX上のアコーディオン描画順と一致させる必要がある
    const accordionSections = [
      {
        parentPath: routeParrentPath.salesReport,
        enabled: isSalesReportPageEnabled,
      },
      {
        parentPath: routeParrentPath.ordersData,
        enabled: isOrdersDataPageEnabled,
      },
      { parentPath: routeParrentPath.camelOrder, enabled: true },
      {
        parentPath: routeParrentPath.camelKiosk,
        enabled: isCamelKioskPageEnabled,
      },
      { parentPath: routeParrentPath.menuManagements, enabled: true },
      { parentPath: routeParrentPath.storeManagements, enabled: true },
      { parentPath: routeParrentPath.delivery, enabled: true },
      { parentPath: routeParrentPath.externals, enabled: true },
      { parentPath: routeParrentPath.settings, enabled: true },
    ];

    const visibleSections = accordionSections.filter((s) => s.enabled);
    // 現在のURLパスに一致するセクションのインデックスを取得
    const matchedIndex = visibleSections.findIndex((s) =>
      location.pathname.includes(s.parentPath)
    );

    // マッチしなければ全閉じ（空配列）、マッチすれば該当セクションを開いた状態にする
    const initialIndex = matchedIndex === -1 ? [] : [matchedIndex];
    initialize(initialIndex);
  }, [
    managerAccountMe,
    isSalesReportPageEnabled,
    isOrdersDataPageEnabled,
    isCamelKioskPageEnabled,
    initialize,
  ]);

  return (
    <CmBox bg="primaryDarkBlue" w="100%" h="100vh">
      {/*
       // @ts-ignore TS2590 */}
      <Flex
        ref={logoWrapperRef}
        justifyContent="center"
        alignItems="center"
        p={3}
      >
        <CmLink to={routes.Home.path}>
          <Logo w={150} h={50} />
        </CmLink>
      </Flex>
      <Spacer />
      <CmScrollBox w="100%" height={`calc(100vh - ${logoWrapperHeight}px)`}>
        {managerAccountMe !== undefined && (
          <CmAccordion
            allowMultiple
            reduceMotion
            index={expandedIndices}
            onChange={(index) =>
              setExpandedIndices(Array.isArray(index) ? index : [index])
            }
          >
            <AccordionLink
              py={3}
              to={Home.path}
              isCurrentPage={location.pathname === '/'}
            >
              <CmFlex color="white">
                <span
                  style={{
                    color: 'white',
                    marginLeft: '-5px',
                    marginRight: '5px',
                  }}
                  className="material-symbols-outlined white"
                >
                  {/**
                   * 本当はchacraのiconで使えるものがなくこちらを使用した。下記サイトでうまく変換ができるかもだが、できずにマテリアルアイコンを使用。。
                   * https://jakearchibald.github.io/svgomg/
                   * **/}
                  home
                </span>
                {Home.title}
              </CmFlex>
            </AccordionLink>
            {isSalesReportPageEnabled && (
              <CustomAccordionItem
                icon={<ChartIcon />}
                category={'売上レポート(β)'}
                panel={
                  <AccordionPanel childPageList={salesReport.childPageList} />
                }
              />
            )}
            {isOrdersDataPageEnabled && (
              <CustomAccordionItem
                icon={<ChartIcon />}
                category={'注文データ'}
                panel={
                  <AccordionPanel childPageList={ordersData.childPageList} />
                }
              />
            )}
            <CustomAccordionItem
              icon={<ShippingBagIcon marginLeft="-2px" />}
              category={'Camel Order設定'}
              panel={
                <AccordionPanel childPageList={camelOrder.childPageList} />
              }
            />
            {/* TODO:yono 本番環境ではCamel Kiosk設定を表示しない。kiosk 1stリリース後に本番環境でも表示する */}
            {isCamelKioskPageEnabled && (
              <CustomAccordionItem
                icon={<ShippingBagIcon marginLeft="-2px" />}
                category={'Camel Kiosk設定'}
                panel={
                  <AccordionPanel childPageList={camelKiosk.childPageList} />
                }
              />
            )}
            <MenuManagementsAccordionItem
              icon={<DishIcon />}
              {...{ associateBrandSelectInput }}
              panel={
                <AccordionPanel childPageList={menuManagement.childPageList} />
              }
            />
            <AccordionLink
              py={3}
              to={LinkItems.path}
              isDisabled={!canUseLinkItems}
              isCurrentPage={location.pathname.includes(LinkItems.path)}
            >
              <TextIcon
                icon={<LayerGroupIcon />}
                color={!canUseLinkItems ? 'gray.400' : 'white'}
              >
                {LinkItems.title}
              </TextIcon>
            </AccordionLink>
            {canUseLinkItemsV2 && (
              <AccordionLink
                py={3}
                to={LinkItemsV2.path}
                isDisabled={!canUseLinkItems}
                isCurrentPage={location.pathname.includes(LinkItemsV2.path)}
              >
                <TextIcon
                  icon={<LayerGroupIcon />}
                  color={!canUseLinkItems ? 'gray.400' : 'white'}
                >
                  {LinkItemsV2.title}
                </TextIcon>
              </AccordionLink>
            )}
            <CustomAccordionItem
              icon={<StoreIcon />}
              category={'店舗管理'}
              panel={<AccordionPanel childPageList={store.childPageList} />}
            />

            <CustomAccordionItem
              icon={<DeliveryIcon />}
              category={'配達設定'}
              panel={<AccordionPanel childPageList={delivery.childPageList} />}
            />
            <CustomAccordionItem
              icon={<ExternalLinkIcon />}
              category={'外部連携設定'}
              panel={<AccordionPanel childPageList={external.childPageList} />}
            />
            <CustomAccordionItem
              icon={<SettingsIcon />}
              category={'設定'}
              panel={<AccordionPanel childPageList={setting.childPageList} />}
            />
          </CmAccordion>
        )}
      </CmScrollBox>
    </CmBox>
  );
};
